import { cn } from './cn.js'

/**
 * Structurally what `nutrientProgress()` in @gramas/shared returns. Declared
 * here rather than imported so this package stays dependency-free — it is a
 * presentation layer and has no business knowing about zod or macro math.
 */
export interface NutrientProgressLike {
  consumed: number | null
  target: number | null
  fraction: number | null
  status: 'none' | 'under' | 'met' | 'over'
  direction: 'at_least' | 'at_most' | 'around'
}

export interface NutrientMeterProps {
  label: string
  progress: NutrientProgressLike
  unit?: string
  /**
   * Decimals on the figure. Salt is the one nutrient every label in the shop
   * states to 0,1 g; the rest are read as whole grams, and the caller knows
   * which is which — `NUTRIENT_DECIMALS` in @gramas/shared has the table.
   */
  decimals?: number
  /**
   * Stack the label over the figure instead of setting them on one line. What
   * four columns cost is width, and a label and a number competing for the same
   * 88 px is where a row of these starts truncating.
   */
  compact?: boolean
  className?: string
}

/**
 * One nutrient against its target, coloured by whether that target is a floor
 * or a ceiling.
 *
 * A filled bar means opposite things for fibre and for salt, so the colour has
 * to come from the graded status rather than from the fraction. Nothing here
 * ever congratulates you for reaching a limit: a ceiling stays neutral all the
 * way up and only turns red past it.
 */
export function NutrientMeter({
  label,
  progress,
  unit = 'g',
  decimals = 0,
  compact = false,
  className,
}: NutrientMeterProps) {
  const { consumed, target, fraction, status, direction } = progress
  const width = fraction == null ? 0 : fraction * 100

  const colour =
    status === 'over'
      ? 'var(--g-danger)'
      : status === 'met' && direction === 'at_least'
        ? 'var(--g-accent)'
        : 'var(--g-dim)'

  const figure = (
    <>
      {consumed == null ? '—' : format(consumed, decimals)}
      {target != null && target > 0 && (
        <span className="text-mute">
          {compact ? '/' : ' / '}
          {format(target, decimals)}
          {compact ? '' : ` ${unit}`}
        </span>
      )}
    </>
  )

  return (
    <div className={cn('flex flex-col', compact ? 'gap-1' : 'gap-1 py-2', className)}>
      {compact ? (
        <>
          <span className="text-[0.6875rem] leading-tight text-mute">{label}</span>
          <span className="tnum text-sm leading-none text-ink">{figure}</span>
        </>
      ) : (
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm text-ink">{label}</span>
          <span className="tnum text-sm text-ink">{figure}</span>
        </div>
      )}
      <div className="h-[3px] w-full overflow-hidden rounded-full bg-line">
        <div
          className="h-full rounded-full"
          style={{
            width: `${width}%`,
            background: colour,
            transition: 'width 200ms var(--ease-out-quick)',
          }}
        />
      </div>
    </div>
  )
}

/**
 * The figure, at the precision the nutrient is actually stated to.
 *
 * This used to guess from the magnitude — anything under ten grams got a
 * decimal — which put "6,0" next to a protein figure of "142" on the same row
 * of meters, and switched a nutrient's precision halfway through the day as it
 * crossed ten. Precision belongs to the nutrient, not to how much of it has
 * been eaten so far.
 */
function format(value: number, decimals: number): string {
  return value.toFixed(decimals).replace('.', ',')
}

export interface NutrientGridProps {
  children: React.ReactNode
  /**
   * `4` puts the whole secondary set on one line — they are read as a set
   * ("anything red today?"), and a set that wraps is scanned twice.
   */
  columns?: 2 | 4
  className?: string
}

export function NutrientGrid({ children, columns = 2, className }: NutrientGridProps) {
  return (
    <div
      className={cn(
        'grid',
        columns === 4 ? 'grid-cols-4 gap-x-2.5' : 'grid-cols-1 gap-x-5 min-[380px]:grid-cols-2',
        className,
      )}
    >
      {children}
    </div>
  )
}
